/**
 * Endpoints #87-#93 — Content Performance (SPEC `Content Performance`).
 *
 * **This file is the one sanctioned home for impressions/views in the contract package.** The PRD
 * hard rule is re-scoped, not dropped: impressions cannot be *scraped*, so no `posts` column and no
 * scraped DTO ever gains such a field. They arrive here only from the user's own LinkedIn analytics
 * export, and live in `content_*` DDL, this file, and the `modules/content` + `api` content glue.
 *
 * Two rules shape every type below:
 *
 * - **No invented data.** `null` means "the export did not state it" and renders as a sentence or a
 *   gap, never as `0`. Series that cannot exist at the requested granularity ship
 *   `{available:false, reasonDisplay}` instead of a zero-filled array; buckets a source row never
 *   covered ship `null`, so the chart gaps.
 * - **Dumb UI (§12.5).** Every displayed number is an `Explained`/`MultiplierDTO`; `value` exists
 *   for chart geometry only. Bucket labels, "estimated" labels, the totals row's as-of line and
 *   every caveat are server-built strings rendered verbatim.
 */
import { z } from 'zod';
import { BUCKET_GRANULARITIES, EXPORT_FORMATS, SORT_DIRS } from '../enums.js';
import { BooleanParamSchema, MAX_POSTS_LIMIT } from './common.js';
/** The three screen modes; `?mode=` and the persisted blob speak this vocabulary (§12.4). */
export const CONTENT_MODES = ['metrics', 'posts', 'comments'];
/**
 * Array order is the default column order (SPEC § Posts mode). Every metric is followed by its
 * dedicated × column, the seven per-post rates close the table as one trailing group, and the whole
 * set is visible by default — the table exists to be read across, and a user who wants it narrower
 * hides columns rather than hunting for them.
 */
export const CONTENT_POSTS_COLUMNS = [
    {
        id: 'post',
        label: 'Post',
        description: 'The post. Enriched with the tracked post text when the app already scrapes it; otherwise the export placeholder plus a link to LinkedIn.',
        kind: 'preview',
        defaultVisible: true,
        sortable: false,
        align: 'left',
    },
    {
        id: 'postedAt',
        label: 'Posted at',
        description: 'When the post went live, as the export reported it, in the app timezone.',
        kind: 'date',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'impressions',
        label: 'Impressions',
        description: 'Impressions LinkedIn reported for the post.',
        kind: 'metric',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'impressionsX',
        label: 'I ×',
        exportLabel: 'Impressions ×',
        description: 'Impressions divided by the median across all imported posts.',
        kind: 'multiplier',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'membersReached',
        label: 'Members reached',
        description: 'Unique members LinkedIn reported reaching with the post.',
        kind: 'metric',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'membersReachedX',
        label: 'MR ×',
        exportLabel: 'Members reached ×',
        description: 'Members reached divided by the median across all imported posts.',
        kind: 'multiplier',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'reactions',
        label: 'Reactions',
        description: 'Reactions as of the latest imported snapshot of this post.',
        kind: 'metric',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'reactionsX',
        label: 'R ×',
        exportLabel: 'Reactions ×',
        description: 'Reactions divided by the median across all imported posts.',
        kind: 'multiplier',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'comments',
        label: 'Comments',
        description: 'Comments as of the latest imported snapshot of this post.',
        kind: 'metric',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'commentsX',
        label: 'C ×',
        exportLabel: 'Comments ×',
        description: 'Comments divided by the median across all imported posts.',
        kind: 'multiplier',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'reposts',
        label: 'Reposts',
        description: 'Reposts as of the latest imported snapshot of this post.',
        kind: 'metric',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'repostsX',
        label: 'Rp ×',
        exportLabel: 'Reposts ×',
        description: 'Reposts divided by the median across all imported posts.',
        kind: 'multiplier',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'saves',
        label: 'Saves',
        description: 'Saves as of the latest imported snapshot of this post.',
        kind: 'metric',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'savesX',
        label: 'Sv ×',
        exportLabel: 'Saves ×',
        description: 'Saves divided by the median across all imported posts.',
        kind: 'multiplier',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'sends',
        label: 'Sends',
        exportLabel: 'Sends on LinkedIn',
        description: 'Times the post was sent in a LinkedIn message.',
        kind: 'metric',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'sendsX',
        label: 'Sd ×',
        exportLabel: 'Sends ×',
        description: 'Sends divided by the median across all imported posts.',
        kind: 'multiplier',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'engagements',
        label: 'Engagements',
        exportLabel: 'Social engagements',
        description: 'LinkedIn\'s own social-engagement total for the post.',
        kind: 'metric',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'engagementsX',
        label: 'E ×',
        exportLabel: 'Engagements ×',
        description: 'Engagements divided by the median across all imported posts.',
        kind: 'multiplier',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'profileViewers',
        label: 'Profile viewers',
        description: 'Profile views LinkedIn attributed to this post, lifetime.',
        kind: 'metric',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'profileViewersX',
        label: 'PV ×',
        exportLabel: 'Profile viewers ×',
        description: 'Profile viewers divided by the median across all imported posts.',
        kind: 'multiplier',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'followersGained',
        label: 'Followers gained',
        description: 'Followers LinkedIn attributed to this post, lifetime.',
        kind: 'metric',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'followersGainedX',
        label: 'F ×',
        exportLabel: 'Followers gained ×',
        description: 'Followers gained divided by the median across all imported posts.',
        kind: 'multiplier',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'inNetworkPct',
        label: 'In/Out Network',
        exportLabel: 'In / out of network %',
        description: 'Share of impressions from your network versus outside it, as LinkedIn reported.',
        kind: 'share',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'capturedAt',
        label: 'As of',
        exportLabel: 'Snapshot captured at',
        description: 'When the import that produced this row captured the post.',
        kind: 'date',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'profileVisitRate',
        label: 'Profile visit rate',
        exportLabel: 'Profile visit rate %',
        description: 'Profile viewers as a percentage of impressions — how often an impression became a profile visit.',
        kind: 'rate',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'followersRate',
        label: 'Followers rate',
        exportLabel: 'Followers rate %',
        description: 'Followers gained as a percentage of impressions — how often an impression became a follower.',
        kind: 'rate',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'reactionsRate',
        label: 'Reactions rate',
        exportLabel: 'Reactions rate %',
        description: 'Reactions as a percentage of impressions — how often an impression became a reaction.',
        kind: 'rate',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'commentsRate',
        label: 'Comments rate',
        exportLabel: 'Comments rate %',
        description: 'Comments as a percentage of impressions — how often an impression became a comment.',
        kind: 'rate',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'repostsRate',
        label: 'Reposts rate',
        exportLabel: 'Reposts rate %',
        description: 'Reposts as a percentage of impressions — how often an impression became a repost.',
        kind: 'rate',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'savesRate',
        label: 'Saves rate',
        exportLabel: 'Saves rate %',
        description: 'Saves as a percentage of impressions — how often an impression became a save.',
        kind: 'rate',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'sendsRate',
        label: 'Sends rate',
        exportLabel: 'Sends rate %',
        description: 'Sends as a percentage of impressions — how often an impression became a send.',
        kind: 'rate',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
];
export const CONTENT_POSTS_COLUMN_IDS = CONTENT_POSTS_COLUMNS.map((c) => c.id);
export const DEFAULT_VISIBLE_CONTENT_POSTS_COLUMNS = CONTENT_POSTS_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.id);
/** Sortable ids as a literal tuple so it can drive `z.enum`; provably a subset of the column ids. */
export const CONTENT_POSTS_SORT_KEYS = [
    'postedAt',
    'impressions',
    'impressionsX',
    'membersReached',
    'membersReachedX',
    'reactions',
    'reactionsX',
    'comments',
    'commentsX',
    'reposts',
    'repostsX',
    'saves',
    'savesX',
    'sends',
    'sendsX',
    'engagements',
    'engagementsX',
    'profileViewers',
    'profileViewersX',
    'followersGained',
    'followersGainedX',
    'inNetworkPct',
    'capturedAt',
    'profileVisitRate',
    'followersRate',
    'reactionsRate',
    'commentsRate',
    'repostsRate',
    'savesRate',
    'sendsRate',
];
export const DEFAULT_CONTENT_POSTS_SORT = {
    key: 'postedAt',
    dir: 'desc',
};
export function getContentPostsColumn(id) {
    const found = CONTENT_POSTS_COLUMNS.find((c) => c.id === id);
    if (!found)
        throw new Error(`unknown content posts column: ${id}`);
    return found;
}
export const CONTENT_COMMENTS_COLUMNS = [
    {
        id: 'comment',
        label: 'Comment',
        description: 'The comment text as the export captured it.',
        kind: 'preview',
        defaultVisible: true,
        sortable: false,
        align: 'left',
    },
    {
        id: 'postAuthor',
        label: 'Post author',
        description: 'Who published the post you commented on.',
        kind: 'link',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'commentedAt',
        label: 'Commented at',
        description: 'When the comment was posted, in the app timezone.',
        kind: 'date',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'impressions',
        label: 'Impressions',
        description: 'Impressions LinkedIn reported for the comment.',
        kind: 'metric',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'impressionsX',
        label: 'I ×',
        exportLabel: 'Impressions ×',
        description: 'Impressions divided by the median across all imported comments.',
        kind: 'multiplier',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'isReply',
        label: 'Reply',
        description: 'Whether the comment replies to another comment rather than to the post.',
        kind: 'boolean',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'post',
        label: 'Post',
        description: 'The post the comment is on.',
        kind: 'link',
        defaultVisible: true,
        sortable: true,
        align: 'left',
    },
    {
        id: 'capturedAt',
        label: 'As of',
        exportLabel: 'Snapshot captured at',
        description: 'When the import that produced this row captured the comment.',
        kind: 'date',
        defaultVisible: false,
        sortable: true,
        align: 'left',
    },
];
export const CONTENT_COMMENTS_COLUMN_IDS = CONTENT_COMMENTS_COLUMNS.map((c) => c.id);
export const DEFAULT_VISIBLE_CONTENT_COMMENTS_COLUMNS = CONTENT_COMMENTS_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.id);
export const CONTENT_COMMENTS_SORT_KEYS = [
    'commentedAt',
    'impressions',
    'impressionsX',
    'isReply',
    'postAuthor',
    'post',
    'capturedAt',
];
export const DEFAULT_CONTENT_COMMENTS_SORT = {
    key: 'commentedAt',
    dir: 'desc',
};
export function getContentCommentsColumn(id) {
    const found = CONTENT_COMMENTS_COLUMNS.find((c) => c.id === id);
    if (!found)
        throw new Error(`unknown content comments column: ${id}`);
    return found;
}
export const ContentMetricsQuerySchema = z.object({
    /** Absent = all time (`from = 0`, clamped to the data). */
    from: z.coerce.number().int().optional(),
    to: z.coerce.number().int().optional(),
    /** Weekly is the only granularity where every Overview series renders (SPEC decision 14). */
    granularity: z.enum(BUCKET_GRANULARITIES).default('week'),
});
/* ========================================================================== */
/*  #88 / #89 — table pages                                                   */
/* ========================================================================== */
const TableRangeSchema = z.object({
    from: z.coerce.number().int().optional(),
    to: z.coerce.number().int().optional(),
    q: z.string().trim().max(200).optional(),
    offset: z.coerce.number().int().nonnegative().default(0),
    limit: z.coerce.number().int().positive().max(MAX_POSTS_LIMIT).default(MAX_POSTS_LIMIT),
});
/** #88 `GET /api/content/posts`. */
export const ContentPostsQuerySchema = TableRangeSchema.extend({
    sort: z.enum(CONTENT_POSTS_SORT_KEYS).default(DEFAULT_CONTENT_POSTS_SORT.key),
    dir: z.enum(SORT_DIRS).default(DEFAULT_CONTENT_POSTS_SORT.dir),
});
/** #89 `GET /api/content/comments`. */
export const ContentCommentsQuerySchema = TableRangeSchema.extend({
    sort: z.enum(CONTENT_COMMENTS_SORT_KEYS).default(DEFAULT_CONTENT_COMMENTS_SORT.key),
    dir: z.enum(SORT_DIRS).default(DEFAULT_CONTENT_COMMENTS_SORT.dir),
});
/* ========================================================================== */
/*  #90 / #91 / #92 — import, sessions                                        */
/* ========================================================================== */
/** Upload/parse caps (SPEC § Import pipeline) — the decompression-bomb guard. */
export const CONTENT_MAX_UPLOAD_BYTES = 50 * 1024 * 1024;
export const CONTENT_MAX_ZIP_ENTRIES = 5_000;
export const CONTENT_MAX_ENTRY_BYTES = 20 * 1024 * 1024;
export const CONTENT_MAX_TOTAL_BYTES = 200 * 1024 * 1024;
/** `?confirm=1` overrides the handle-clash gate; anything else leaves it armed. */
export const ContentImportQuerySchema = z.object({
    confirm: BooleanParamSchema.optional(),
});
/* ========================================================================== */
/*  #93 — export                                                              */
/* ========================================================================== */
export const CONTENT_EXPORT_TABLES = ['posts', 'comments'];
/**
 * #93 `GET /api/content/export`. JSON exports the whole merged dataset in its **native shapes**
 * (daily points and weekly spans verbatim, `partial` included, full snapshot history); CSV exports
 * the merged latest rows of one table with the #29 conventions.
 */
export const ContentExportQuerySchema = z.object({
    format: z.enum(EXPORT_FORMATS).default('json'),
    table: z.enum(CONTENT_EXPORT_TABLES).default('posts'),
    /** CSV only: visible column ids, in display order. Absent = the table's default visible set. */
    columns: z
        .string()
        .transform((raw) => raw
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part.length > 0))
        .pipe(z.array(z.string().max(64)).min(1))
        .optional(),
    /**
     * A sort key of whichever table `table` names — the two vocabularies overlap but are not equal,
     * so the route narrows it with `contentPostsSortKeyOr` / `contentCommentsSortKeyOr` rather than
     * this schema, which cannot see `table` while it validates a sibling field.
     */
    sort: z.string().max(64).optional(),
    dir: z.enum(SORT_DIRS).default('desc'),
    q: z.string().trim().max(200).optional(),
    from: z.coerce.number().int().optional(),
    to: z.coerce.number().int().optional(),
});
/* --------------------------------------------------------------- narrowing */
/**
 * The four helpers the export route needs to turn free-form `sort=`/`columns=` into the closed
 * vocabularies the repo and the CSV writer accept. Unknown values fall back rather than 400: an
 * export link a user bookmarked before a column was renamed should still produce a file.
 */
export function contentPostsSortKeyOr(raw, fallback = DEFAULT_CONTENT_POSTS_SORT.key) {
    const found = CONTENT_POSTS_SORT_KEYS.find((key) => key === raw);
    return found ?? fallback;
}
export function contentCommentsSortKeyOr(raw, fallback = DEFAULT_CONTENT_COMMENTS_SORT.key) {
    const found = CONTENT_COMMENTS_SORT_KEYS.find((key) => key === raw);
    return found ?? fallback;
}
/** Keeps the caller's order, drops unknown and repeated ids; empty input → the default set. */
export function sanitizeContentPostsColumns(raw) {
    const kept = [...new Set(raw ?? [])].filter((id) => CONTENT_POSTS_COLUMN_IDS.includes(id));
    return kept.length === 0 ? DEFAULT_VISIBLE_CONTENT_POSTS_COLUMNS : kept;
}
export function sanitizeContentCommentsColumns(raw) {
    const kept = [...new Set(raw ?? [])].filter((id) => CONTENT_COMMENTS_COLUMN_IDS.includes(id));
    return kept.length === 0 ? DEFAULT_VISIBLE_CONTENT_COMMENTS_COLUMNS : kept;
}
//# sourceMappingURL=content.js.map